import { ApiResponse } from './types';
import { appConfig } from '../../config/app-config';
import { useSupabaseAuthStore } from '@/stores/supabaseAuthStore';

export abstract class BaseService {
  protected baseUrl: string;
  protected serviceName: string;

  constructor(serviceName: string) {
    this.baseUrl = appConfig.apiBaseUrl;
    this.serviceName = serviceName;
  }

  protected log(action: string, data?: any): void {
    if (appConfig.enableDebugLogs) {
      console.log(`[REAL] ${this.serviceName}.${action}`, data);
    }
  }

  protected async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    // Get auth token from the current session
    const { session } = useSupabaseAuthStore.getState();
    try {
      const url = `${this.baseUrl}${endpoint}`;
      this.log('makeRequest', { url, options });

      const token = session?.access_token;

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options?.headers,
          // Only set Content-Type for non-FormData requests
          ...(options?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If response is not JSON, use the default error message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      return {
        data: data.data || data,
        success: true,
        message: data.message || 'Request successful',
      };
    } catch (error) {
      console.error(`${this.serviceName} API Request failed:`, error);
      return {
        data: null as any,
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}
